/**
 * Page entry point: controls in, worker out, a scene that fills in as it goes.
 *
 * The worker no longer answers a single request with a single picture. It streams:
 * the boundary, then the city labels, then the relief at preview resolution — first
 * terrain alone, then again as each band of the raster decodes — and finally the
 * full-resolution fields. This module relays those stages straight into the scene,
 * which is why pressing *Render* puts a board on the paper immediately and then
 * watches the country grow into it rather than staring at an empty stage for three
 * minutes.
 *
 * Everything the worker hands over is camera- and light-independent, so orbiting,
 * relighting and refocusing never re-enter it.
 */

import { resolveCountry } from "../shared/countries.ts";
import {
  APERTURE,
  EXPOSURE,
  SUN_AZIMUTH_DEG,
  SUN_ELEVATION_DEG,
} from "../shared/constants.ts";
import { DEFAULT_CONFIG } from "../shared/types.ts";
import type { Country, RenderConfig, View } from "../shared/types.ts";
import { createScene } from "./scene/scene.ts";
import {
  elements,
  readConfig,
  readFinish,
  renderLabels,
  setBusy,
  setCredit,
  setProgress,
  setStats,
  setTitleBlock,
} from "./ui.ts";
import type { WorkerRequest, WorkerResponse } from "./worker.ts";

type RenderDone = Extract<WorkerResponse, { type: "done" }>;
type RenderOutline = Extract<WorkerResponse, { type: "outline" }>;
type RenderStage = Extract<WorkerResponse, { type: "stage" }>;
type RenderCities = Extract<WorkerResponse, { type: "cities" }>;

interface Stream {
  outline(message: RenderOutline): void;
  cities(message: RenderCities): void;
  stage(message: RenderStage): void;
}

const round = (value: number): string => value.toLocaleString("it-IT", { maximumFractionDigits: 0 });

const scene = createScene(elements.canvas);
scene.onLabels(renderLabels);

let worker: Worker | null = null;
let shown: { country: Country; config: RenderConfig } | null = null;
/** Which preset the camera was last flown to; only the filename reads it. */
let preset: View = DEFAULT_CONFIG.view;

/** One worker serves every render; a crashed one is replaced on the next run. */
function runPipeline(country: string, config: RenderConfig, stream: Stream): Promise<RenderDone> {
  const { promise, resolve, reject } = Promise.withResolvers<RenderDone>();
  // `./worker.js`, not `.ts`: Bun leaves this URL untouched in the bundle, so
  // it has to name the emitted worker entrypoint sitting next to this file.
  const active = (worker ??= new Worker(new URL("./worker.js", import.meta.url), {
    type: "module",
  }));
  const detach = () => {
    active.removeEventListener("message", onMessage);
    active.removeEventListener("error", onError);
  };
  const onMessage = (event: MessageEvent<WorkerResponse>) => {
    const message = event.data;
    switch (message.type) {
      case "progress":
        setProgress(message.progress.stage, message.progress.fraction);
        return;
      case "outline":
        stream.outline(message);
        return;
      case "cities":
        stream.cities(message);
        return;
      case "stage":
        stream.stage(message);
        return;
      case "error":
        detach();
        reject(new Error(message.message));
        return;
      case "done":
        detach();
        resolve(message);
        return;
    }
  };
  const onError = (event: ErrorEvent) => {
    detach();
    active.terminate();
    if (worker === active) worker = null;
    reject(new Error(event.message || "Il worker si è interrotto."));
  };
  active.addEventListener("message", onMessage);
  active.addEventListener("error", onError);
  const request: WorkerRequest = { type: "render", country, config };
  active.postMessage(request);
  return promise;
}

async function render(): Promise<void> {
  let query: string;
  let config: RenderConfig;
  let country: Country;
  try {
    ({ country: query, config } = readConfig());
    country = resolveCountry(query);
  } catch (error) {
    setProgress(error instanceof Error ? error.message : String(error));
    return;
  }

  setBusy(true);
  elements.download.disabled = true;
  setStats("");
  setProgress(`Preparo ${country.name}…`, 0);
  let resolved = country;
  try {
    const done = await runPipeline(query, config, {
      outline: (message) => {
        resolved = message.country;
        preset = config.view;
        scene.setOutline(message.rings, config.view, config.title);
        setTitleBlock(message.country.name, config.title);
        setCredit(config.year);
      },
      cities: (message) => scene.setCities(message.cities),
      stage: (message) => scene.setRelief(message.fields),
    });

    scene.setRelief(done.fields);
    shown = { country: resolved, config };
    elements.download.disabled = false;
    setProgress(`${resolved.name} pronta`, 1);
    setStats(
      `${round(done.stats.spikes)} spike da ${round(done.stats.populationCells)} celle · ` +
        `densità P99 ${round(done.stats.densityQ99)} ab./km²`,
    );
  } catch (error) {
    setProgress(error instanceof Error ? error.message : String(error));
  } finally {
    setBusy(false);
  }
}

async function download(): Promise<void> {
  if (!shown) return;
  const { country, config } = shown;
  elements.download.disabled = true;
  try {
    const blob = await scene.snapshot(
      config.width,
      config.height,
      {
        countryName: country.name,
        title: config.title,
        year: config.year,
        hint: "Trascina per ruotare · rotella per lo zoom",
      },
      (fraction) => setProgress("Accumulo il fotogramma finale", fraction),
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${country.slug}-${preset}.png`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    setProgress(`${country.name} pronta`, 1);
  } catch (error) {
    setProgress(error instanceof Error ? error.message : String(error));
  } finally {
    elements.download.disabled = false;
  }
}

function goTo(view: View): void {
  preset = view;
  scene.goTo(view);
}

/** The three optical controls, read together because they share one handler. */
function applyFinish(): void {
  const finish = readFinish();
  scene.setExposure(finish.exposure);
  scene.setAperture(finish.aperture);
  scene.setSun(finish.sunAzimuth, finish.sunElevation);
}

elements.render.addEventListener("click", () => void render());
elements.download.addEventListener("click", () => void download());
elements.viewTop.addEventListener("click", () => goTo("top"));
elements.viewPerspective.addEventListener("click", () => goTo("perspective"));
elements.country.addEventListener("keydown", (event) => {
  if (event.key === "Enter" && !elements.render.disabled) void render();
});
for (const input of [
  elements.exposure,
  elements.aperture,
  elements.sunAzimuth,
  elements.sunElevation,
]) {
  input.addEventListener("input", applyFinish);
}
window.addEventListener("resize", () => scene.resize());

elements.exposure.value = String(EXPOSURE);
elements.aperture.value = String(APERTURE);
elements.sunAzimuth.value = String(SUN_AZIMUTH_DEG);
elements.sunElevation.value = String(SUN_ELEVATION_DEG);
setCredit(DEFAULT_CONFIG.year);
elements.download.disabled = true;
setProgress("Pronto.");
